import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  AlertCircle,
  ArrowRight,
  Award,
  BookOpen,
  Building2,
  CheckCircle2,
  ClipboardList,
  ExternalLink,
  FileText,
  Folder,
  GitBranch,
  Loader2,
  MessageSquare,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import RefreshFailedNotice from '../../components/RefreshFailedNotice'
import { formatDateTime, formatNumber } from '../../lib/format'
import { adminOverviewResponse } from '../../lib/types'
import {
  greetingName,
  timeOfDay,
  fmtRelative,
  statusTrendSeries,
  taskHealthPartition,
  formatDayMonth,
} from '../../lib/dashboard-helpers'
import { useAuthStore } from '../../stores/authStore'
import { Avatar } from '../../components/Avatar'
import {
  DonutChart,
  Legend,
  RankedBarChart,
  TrendChart,
} from '../../components/charts/Charts'
import { ProgressRing } from '../../lib/goals-ui'
import { useChartHover } from '../../components/charts/use-chart-hover'
import { ChartTooltip } from '../../components/charts/ChartTooltip'
import { DONUT_PALETTE, TASK_HEALTH_COLORS } from '../../components/charts/palette'
import { EmptyStateBanner } from '../../components/EmptyStateBanner'

/**
 * SubAdminDashboard — a permission-driven dashboard for the `sub_admin`
 * role. Unlike the Company Admin's DashboardPage, this view renders
 * each section only when the actor's `subAdminOperations` grant array
 * includes a matching slug.
 *
 * Five-section recipe (every section is permission-gated):
 *   1. Top 4 KPI cards — one per visible grant, capped at 4
 *   2. Today / Action Needed — At-Risk Tasks + Missing Reports
 *   3. Role-specific workload chart — picks ONE based on strongest grant
 *   4. Recent Activity — filtered by viewable categories
 *   5. Quick Actions — only the ones their grants allow
 *
 * Zero-grants Sub Admins see a polite empty state explaining that the
 * Company Admin hasn't delegated any operations yet.
 */

// Local tone palette — clone of the relevant subset from DashboardPage
// so this file can be deleted/refactored without touching the Company
// Admin page. (TONE_CLASSES isn't exported from DashboardPage.)
// Values mirror DashboardPage's TONE_CLASSES exactly: a soft per-tone wash in
// dark mode (the old uniform white/6% neutral tile left linear glyphs nearly
// invisible — QA-reported, fixed there first), and `amber` is the ORANGE family
// while `gold` is the yellow one, matching every other dashboard.
const TONE_CLASSES = {
  violet:
    'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-400/20',
  sky: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-400/20',
  emerald:
    'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-400/20',
  amber:
    'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-400/20',
  slate:
    'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
  rose: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-400/20',
  gold: 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20',
}

const HALO_CLASSES = {
  violet: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  sky: 'from-[#DBEAFE] to-transparent dark:from-transparent',
  emerald: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  amber: 'from-[#FFEDD5] to-transparent dark:from-transparent',
  rose: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  gold: 'from-[#FEF3C7] to-transparent dark:from-transparent',
  slate: 'from-[color:var(--color-canvas)] to-transparent',
}

/** Permission helper — true iff the user has been granted the given
 *  Sub Admin operation slug. Pure, no store dependency. */
function hasOp(user, op) {
  return (
    Array.isArray(user?.subAdminOperations) &&
    user.subAdminOperations.includes(op)
  )
}

// ---------- KPI tile ----------

function KpiTile({ label, value, icon: Icon, tone, hint, to }) {
  const tileClass = TONE_CLASSES[tone] ?? TONE_CLASSES.slate
  const haloClass = HALO_CLASSES[tone] ?? HALO_CLASSES.slate
  const body = (
    <>
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          haloClass
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* 2-line label floor (matches the Company Admin / User tiles) so a
           *  wrapping label like "Reports Compliance · Today" doesn't push its
           *  value below the single-line tiles in the same row. */}
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0 min-h-[2.5rem] leading-tight">
            {label}
          </h3>
          <p className="mt-2 font-display text-4xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
            {typeof value === 'number' ? formatNumber(value) : value}
          </p>
          {hint ? (
            <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)]">
              {hint}
            </p>
          ) : null}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tileClass
          }
        >
          <Icon size={24} />
        </div>
      </div>
      {to ? (
        <div className="relative mt-auto pt-3 inline-flex items-center gap-1 text-[13px] font-normal text-[color:var(--color-accent-strong)] opacity-80 group-hover:opacity-100">
          View
          <ArrowRight
            size={12}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </div>
      ) : null}
    </>
  )
  const wrapperClass =
    'flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5'
  return to ? (
    <Link
      to={to}
      className={
        'group ' +
        wrapperClass +
        ' hover:border-[color:var(--color-accent-border)] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer'
      }
    >
      {body}
    </Link>
  ) : (
    <div className={wrapperClass}>{body}</div>
  )
}

/** Section heading — same visual rhythm as the Company Admin page. */
function SectionHeader({ title, hint }) {
  return (
    <div className="flex items-baseline justify-between gap-3 mt-10 mb-4">
      <div>
        <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          {title}
        </h2>
        {hint ? (
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  )
}

/** Card chrome with icon header — shared by the Action Needed and
 *  Workload sections so they read as the same family. */
function Card({ icon: Icon, iconTone, title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
      <div className="flex items-center gap-2 mb-3">
        <span
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center ' +
            (TONE_CLASSES[iconTone] ?? TONE_CLASSES.slate)
          }
        >
          <Icon size={20} />
        </span>
        <div>
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
            {title}
          </h3>
          {subtitle ? (
            <p className="text-[13px] text-[color:var(--color-ink-muted)]">
              {subtitle}
            </p>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  )
}

/** Plain analytics card (no icon) — title + optional subtitle header over a
 *  chart body. Mirrors the Company Admin dashboard's AnalyticsCard so the two
 *  admin surfaces read as the same family. */
function AnalyticsPanel({ title, subtitle, className = '', children }) {
  return (
    <div
      className={
        'rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 ' +
        className
      }
    >
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          {title}
        </h3>
        {subtitle ? (
          <p className="text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
            {subtitle}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  )
}

/** Compliance ring — clone of the DashboardPage component, scoped to
 *  this file so the Sub Admin page can evolve independently. */
function ComplianceRing({ rate, label, submitted, eligible, tone }) {
  const { ref, hover, track, clear } = useChartHover()
  const noEligible = rate === null || eligible === 0
  const safeRate = noEligible ? 0 : Math.max(0, Math.min(100, rate))
  const accentColor =
    tone === 'emerald'
      ? 'var(--color-success)'
      : tone === 'amber'
        ? 'var(--color-warning)'
        : tone === 'sky'
          ? 'var(--color-info)'
          : 'var(--color-accent-strong)'
  // Mirrors the Company Admin ComplianceRing: no hover payload when nobody
  // is report-eligible (an empty ring shouldn't pop a "0/0 · 0%" tooltip).
  const ringHover = noEligible
    ? undefined
    : {
        color: accentColor,
        label,
        value: `${formatNumber(submitted)}/${formatNumber(eligible)} · ${safeRate}%`,
      }
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 flex items-center gap-4">
      <div
        ref={ref}
        className="h-20 w-20 rounded-full grid place-items-center shrink-0 relative"
        style={{
          background: noEligible
            ? 'var(--color-canvas)'
            : `conic-gradient(${accentColor} 0% ${safeRate}%, var(--color-canvas) ${safeRate}% 100%)`,
        }}
        onMouseEnter={ringHover ? track(ringHover) : undefined}
        onMouseMove={ringHover ? track(ringHover) : undefined}
        onMouseLeave={ringHover ? clear : undefined}
      >
        <div className="h-14 w-14 rounded-full bg-[color:var(--color-card)] border border-[color:var(--color-rule)] grid place-items-center">
          <span
            className="font-display text-base tabular-nums"
            style={{
              color: noEligible ? 'var(--color-ink-muted)' : accentColor,
            }}
          >
            {noEligible ? '—' : `${safeRate}%`}
          </span>
        </div>
        {hover ? <ChartTooltip {...hover} /> : null}
      </div>
      <div className="min-w-0">
        <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          {label}
        </h3>
        {noEligible ? (
          <>
            <p className="mt-1 text-sm text-[color:var(--color-ink-strong)] font-normal">
              No eligible members
            </p>
            <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
              Add teammates with role <em>User</em> to enable reporting
            </p>
          </>
        ) : (
          <>
            <p className="mt-1 text-sm text-[color:var(--color-ink-strong)] font-normal">
              {formatNumber(submitted)}{' '}
              <span className="text-[color:var(--color-ink-muted)] font-normal">
                of {formatNumber(eligible)} submitted
              </span>
            </p>
            <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
              {safeRate >= 80
                ? 'Healthy'
                : safeRate >= 50
                  ? 'Needs reminders'
                  : 'Reach out to the team'}
            </p>
          </>
        )}
      </div>
    </div>
  )
}

// ---------- At-Risk row ----------

function AtRiskRow({ task }) {
  const reason = (() => {
    const days = Math.max(1, Math.round(task.riskHours / 24))
    if (task.riskReason === 'overdue') {
      return {
        tone: 'rose',
        label: `Overdue · ${days} ${days === 1 ? 'day' : 'days'}`,
      }
    }
    if (task.riskReason === 'due_today') return { tone: 'amber', label: 'Due today' }
    return {
      tone: 'sky',
      label: `Idle · ${days} ${days === 1 ? 'day' : 'days'}`,
    }
  })()
  // Deep-link to the task itself (mirrors the Company Admin fix — a generic
  // filtered Task Monitor URL sent every row's "Open" to the same page,
  // QA-reported). The overview feed ships spaceId + listId per row, and
  // /app/spaces/... allows sub_admin. Filtered-Monitor fallback kept for
  // the defensive case where spaceId is null (the schema allows it).
  let openHref
  if (task.spaceId && task.listId) {
    openHref = `/app/spaces/${task.spaceId}/lists/${task.listId}?task=${task.id}`
  } else {
    const todayIso = new Date().toISOString().slice(0, 10)
    const yesterdayIso = new Date(new Date().getTime() - 86_400_000)
      .toISOString()
      .slice(0, 10)
    openHref = '/task-monitor'
    if (task.riskReason === 'overdue') openHref = `/task-monitor?dueTo=${yesterdayIso}`
    else if (task.riskReason === 'due_today')
      openHref = `/task-monitor?dueFrom=${todayIso}&dueTo=${todayIso}`
  }
  const taskName =
    task.name.length > 60 ? task.name.slice(0, 59) + '…' : task.name
  return (
    <div className="flex items-center gap-3 py-3">
      {task.assigneeName ? (
        <Avatar
          name={task.assigneeName}
          avatarUrl={task.assigneeAvatarUrl}
          size="sm"
          colorByName
        />
      ) : (
        <span
          aria-label="Unassigned"
          className="inline-flex h-7 w-7 rounded-full border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] items-center justify-center text-[11px] shrink-0"
        >
          —
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
          {taskName}
        </p>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <span
            className={
              'inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium ' +
              TONE_CLASSES[reason.tone]
            }
          >
            {reason.label}
          </span>
          {task.assigneeName ? (
            <span className="text-[12px] text-[color:var(--color-ink-muted)] truncate">
              {task.assigneeName}
            </span>
          ) : (
            <span className="text-[12px] text-[color:var(--color-ink-muted)] italic">
              Unassigned
            </span>
          )}
          {task.dueDate ? (
            <span className="text-[12px] text-[color:var(--color-ink-muted)]">
              · Due {formatDateTime(task.dueDate)}
            </span>
          ) : null}
        </div>
      </div>
      <Link
        to={openHref}
        className="shrink-0 inline-flex items-center gap-1 text-[13px] font-medium text-[color:var(--color-accent-strong)] hover:underline"
      >
        Open
        <ExternalLink size={12} aria-hidden="true" />
      </Link>
    </div>
  )
}

// ---------- Workload chart bodies ----------

/** "Tasks by Status" trend — tasks created on each of the last 14 days, split
 *  by the workflow status they are in NOW, one line per status in the
 *  workflow's own colour (custom statuses included). Built from the same
 *  `tasks.statusTrend` payload + `statusTrendSeries` helper as the Company
 *  Admin dashboard so the two roles can't drift. */
function TasksByStatusTrend({ tasks }) {
  const trend = statusTrendSeries(tasks?.statusTrend)
  const createdLast7 = (tasks?.createdSparkline ?? [])
    .slice(-7)
    .reduce((sum, p) => sum + (p.count ?? 0), 0)
  return (
    <AnalyticsPanel title="Tasks by Status" subtitle="Created in last 14 days">
      <TrendChart
        labels={trend.labels}
        series={trend.series}
        labelFormat={formatDayMonth}
        emptyMessage="No tasks created in the last 14 days"
      />
      <div className="mt-3 flex flex-col gap-2 text-[13px] text-[color:var(--color-ink-muted)]">
        <span>
          Created{' '}
          <span className="font-medium text-[color:var(--color-ink-strong)] tabular-nums">
            {formatNumber(createdLast7)}
          </span>{' '}
          in last 7 days
        </span>
        {trend.legend.length > 0 ? (
          <ul
            className="m-0 flex list-none flex-wrap items-center gap-x-5 gap-y-1.5 p-0"
            aria-label="Tasks created in the last 7 days, by current status"
          >
            {trend.legend.map((s) => (
              <li key={s.name} className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: s.color, border: '1px solid var(--chart-outline)' }}
                />
                {s.name}
                <span className="font-medium text-[color:var(--color-ink-strong)] tabular-nums">
                  {formatNumber(s.last7)}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </AnalyticsPanel>
  )
}

/** Task Health donut — a mutually-exclusive partition of `tasks.total` (all
 *  tasks in scope, incl. subtasks): On track (open, not past due) + Overdue
 *  (open AND past due) + Completed (completedAt set), via the shared
 *  `taskHealthPartition` — the same split + colours as the Company Admin
 *  Task Completion 3D pie. The segments sum to `total` and agree with the
 *  Task Completion card beside it.
 *  Previously this summed openCount (the first-column "To Do" count only) +
 *  overdueCount + completedCount — omitting in-progress tasks and
 *  double-counting overdue tasks still in To Do, so its centre read e.g. 198
 *  next to a 158-task total. Titled "Task Health" (not "Tasks by Status") since
 *  these are delivery buckets, not workflow statuses — the User dashboard's
 *  "Tasks by Status" card shows real workflow states. */
function TasksByStatusDonut({ data }) {
  const { completed, overdue, onTrack } = taskHealthPartition(data.tasks)
  const segments = [
    { label: 'On track', value: onTrack, color: TASK_HEALTH_COLORS.onTrack },
    { label: 'Overdue', value: overdue, color: TASK_HEALTH_COLORS.overdue },
    { label: 'Completed', value: completed, color: TASK_HEALTH_COLORS.completed },
  ]
  const total = segments.reduce((s, x) => s + x.value, 0)
  if (total === 0) {
    return (
      <EmptyStateBanner
        icon={ClipboardList}
        title="No tasks yet"
        body="The breakdown chart will appear here once your team starts creating tasks."
        tone="sky"
        compact
      />
    )
  }
  return (
    <div className="flex items-center gap-6">
      <DonutChart
        segments={segments}
        centerLabel="Tasks"
        centerValue={total}
        size={150}
        thickness={24}
      />
      <div className="flex-1 min-w-0">
        <Legend segments={segments} />
      </div>
    </div>
  )
}

function MembersByRoleDonut({ data }) {
  // Count by Work Role (matches the Work Roles page), already ordered by the
  // admin-arranged hierarchy. A user with no Work Role linked is counted under
  // their level (e.g. "Employee"), as on the Members page — merged into the
  // catalogue role of the same name server-side (utils/work-role-breakdown.js).
  const rows = data.members.byWorkRole ?? []
  const segments = rows.map((r, i) => ({
    // Stable, unique React key for the Legend (which keys by `id ?? label`):
    // the catalogue uuid, or a `tier:`-prefixed fallback for a slice holding
    // level-only members (id null).
    id: r.id ?? `tier:${r.name}:${i}`,
    label: r.name,
    value: r.count,
    color: DONUT_PALETTE[i % DONUT_PALETTE.length],
  }))
  const centerValue = segments.reduce((s, x) => s + (x.value ?? 0), 0)
  if (centerValue === 0) {
    return (
      <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center italic">
        No members in roster yet.
      </p>
    )
  }
  return (
    <div className="flex items-center gap-6">
      <DonutChart
        segments={segments}
        centerLabel="Members"
        centerValue={centerValue}
        size={150}
        thickness={24}
      />
      <div className="flex-1 min-w-0">
        <Legend segments={segments} />
      </div>
    </div>
  )
}

function SopUsageBody({ sops }) {
  if (!sops) {
    return (
      <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center italic">
        SOP rollup unavailable for this tenant.
      </p>
    )
  }
  const cells = [
    { label: 'Total SOPs', value: sops.totalCount },
    { label: 'Tasks Generated', value: sops.tasksGenerated },
    { label: 'Pending Approvals', value: sops.pendingApprovals },
  ]
  return (
    <div className="grid grid-cols-3 gap-3">
      {cells.map((c) => (
        <div
          key={c.label}
          className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-3 text-center"
        >
          <p className="font-display text-2xl text-[color:var(--color-ink-strong)] tabular-nums">
            {formatNumber(c.value)}
          </p>
          <p className="mt-1 text-[12px] uppercase tracking-wider text-[color:var(--color-ink-muted)]">
            {c.label}
          </p>
        </div>
      ))}
    </div>
  )
}

// ---------- Activity helpers ----------

function iconForKind(kind) {
  if (kind.startsWith('report.')) return FileText
  if (kind.startsWith('badge.')) return Award
  if (kind.startsWith('task.')) return CheckCircle2
  if (kind.startsWith('comment.')) return MessageSquare
  return Activity
}

function toneForKind(kind) {
  if (kind.startsWith('report.')) return 'sky'
  if (kind.startsWith('badge.')) return 'gold'
  return 'slate'
}

// ---------- Quick Action tile ----------

/** Numbered Quick Action row — visual match for the Company Admin
 *  Dashboard's `QuickActions` component (DashboardPage.jsx). Renders
 *  as a numbered circle + label + right-chevron in a single column,
 *  so the Sub Admin row reads as the same family as the tenant-owner
 *  one. The `tone` + `icon` props are kept on the component contract
 *  but no longer rendered — they were the old visual marker, replaced
 *  here by the numeric step badge for consistency. */
function ActionTile({ n, label, to }) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2.5 hover:border-[color:var(--color-accent-border)] hover:bg-[color:var(--color-accent-bg)] transition-colors cursor-pointer"
    >
      <span className="h-6 w-6 rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)] flex items-center justify-center shrink-0 text-[13px] font-medium tabular-nums">
        {n}
      </span>
      <span className="text-sm font-normal text-[color:var(--color-ink-strong)] flex-1 truncate">
        {label}
      </span>
      <ArrowRight
        size={12}
        className="text-[color:var(--color-ink-muted)] shrink-0 group-hover:text-[color:var(--color-accent-strong)] group-hover:translate-x-0.5 transition-transform"
      />
    </Link>
  )
}

// ---------- Section catalogues ----------

/** Catalogue of KPI tiles keyed by the grant slug that unlocks them.
 *  Each entry is a builder so we can read live data off `data`. The
 *  ordering of this array is the priority ordering — when the user
 *  has more than 4 grants, we slice the top 4 from this list. */
const KPI_CATALOGUE = [
  {
    op: 'task_monitor.view',
    build: (data) => ({
      label: 'To Do',
      value: data.tasks.openCount ?? 0,
      icon: ClipboardList,
      tone: 'violet',
      to: '/task-monitor',
    }),
  },
  {
    op: 'work_reports.view',
    build: (data) => {
      const t = data.reports.dailyToday
      return {
        label: 'Reports Compliance · Today',
        value: t.complianceRate === null ? '—' : `${t.complianceRate}%`,
        icon: FileText,
        tone: 'sky',
        to: '/work-reports',
        hint:
          t.complianceRate === null
            ? 'No eligible members'
            : `${t.submitted} of ${t.eligible} submitted`,
      }
    },
  },
  {
    op: 'members.manage',
    build: (data) => ({
      label: 'Active Members',
      value: data.members.active,
      icon: Users,
      tone: 'emerald',
      to: '/members',
      hint: `${data.members.total} total in roster`,
    }),
  },
  {
    op: 'sops.manage',
    build: (data) => ({
      label: 'SOPs Total',
      value: data.sops?.totalCount ?? 0,
      icon: BookOpen,
      tone: 'amber',
      to: '/sops-library',
      hint: data.sops?.tasksGenerated
        ? `${data.sops.tasksGenerated} tasks generated`
        : 'Library overview',
    }),
  },
  {
    op: 'audit_log.view',
    build: (data) => ({
      // [QA-16] Real audit-log counts from /admin/overview (same tenant
      // filter as the Audit Log page this links to). It used to show the
      // activity feed's length — task/comment/badge events, capped at 12,
      // never an audit row.
      label: 'Audit Events · 7 Days',
      value: data.audit ? data.audit.last7Days : '—',
      icon: ShieldCheck,
      tone: 'slate',
      to: '/audit-log',
      hint: data.audit ? `${data.audit.total} in the audit log` : 'Open the audit log',
    }),
  },
]

/** Catalogue of Quick Action tiles, keyed by the grant slug that
 *  unlocks them. Order here = render order in the numbered list.
 *
 *  The first four entries mirror the Company Admin Dashboard's
 *  `QuickActions` sequence (Create Workspace · Add Department ·
 *  Invite Member · Configure Schedule) so a Sub Admin reading the
 *  list sees the same Day-One setup flow as the tenant owner.
 *  Entries 5+ surface the additional admin areas a Sub Admin can
 *  reach but the Company Admin's onboarding checklist doesn't
 *  include. The sequential step badge (1, 2, 3…) is computed at
 *  render time AFTER filtering by grants, so a Sub Admin with only
 *  a subset of these still sees a clean 1..N numbering. */
const ACTION_CATALOGUE = [
  { op: 'workspaces.manage', icon: Folder, label: 'Create Workspace', to: '/workspaces?action=new', tone: 'violet' },
  { op: 'departments.manage', icon: Building2, label: 'Add Department', to: '/departments?action=new', tone: 'sky' },
  { op: 'members.manage', icon: UserPlus, label: 'Invite Member', to: '/members?action=invite', tone: 'emerald' },
  { op: 'work_reports.view', icon: FileText, label: 'Configure Schedule', to: '/reminders-schedule', tone: 'sky' },
  { op: 'task_monitor.view', icon: ClipboardList, label: 'Open Task Monitor', to: '/task-monitor', tone: 'violet' },
  { op: 'sops.manage', icon: BookOpen, label: 'Open SOPs Library', to: '/sops-library', tone: 'amber' },
  { op: 'workflows.manage', icon: GitBranch, label: 'Manage Workflows', to: '/workflows', tone: 'gold' },
  { op: 'audit_log.view', icon: ShieldCheck, label: 'View Audit Log', to: '/audit-log', tone: 'slate' },
]

/** Tone helper for compliance rings — green when healthy, amber when
 *  borderline, rose when failing. Shared by the three rings. */
function complianceTone(rate) {
  if (rate === null) return 'slate'
  if (rate >= 80) return 'emerald'
  if (rate >= 50) return 'amber'
  return 'rose'
}

// ---------- Body ----------

function SubAdminBody({ data, user }) {
  // ── Section 1: KPI cards (cap 4, priority ordering) ──────────────
  const kpis = KPI_CATALOGUE.filter((k) => hasOp(user, k.op))
    .slice(0, 4)
    .map((k) => ({ key: k.op, ...k.build(data) }))

  // ── Section 2: Today / Action Needed ─────────────────────────────
  const showAtRisk = hasOp(user, 'task_monitor.view')
  const showMissingReports = hasOp(user, 'work_reports.view')
  const atRiskTasks = (data.atRiskTasks ?? []).slice(0, 5)
  const todayReports = data.reports.dailyToday
  const missingCount =
    todayReports.eligible > 0
      ? Math.max(0, todayReports.eligible - todayReports.submitted)
      : 0

  // ── Section 3: Grant-gated analytics ─────────────────────────────
  // Each analytics block renders ONLY when its operation is granted —
  // identical hasOp() gating to the KPI tiles and Action-Needed cards, so
  // a Sub Admin never sees a chart for an area they can't manage. The
  // underlying /admin/overview payload already carries every section's data
  // (the Sub Admin parity rule), so this is purely a visibility decision.
  const showTaskAnalytics = hasOp(user, 'task_monitor.view')
  const showReportAnalytics = hasOp(user, 'work_reports.view')
  const showMemberAnalytics = hasOp(user, 'members.manage')
  const showSopAnalytics = hasOp(user, 'sops.manage')
  const showAnalytics =
    showTaskAnalytics ||
    showReportAnalytics ||
    showMemberAnalytics ||
    showSopAnalytics
  // Task completion + 14-day created/completed series (shown with task grant).
  const taskTotal = data.tasks?.total ?? 0
  const taskCompleted = data.tasks?.completedCount ?? 0
  const dayLabel = formatDayMonth

  // ── Section 4: Recent activity — filter by viewable categories ───
  // Category gating per the brief:
  //   task    → needs task_monitor.view
  //   report  → needs work_reports.view
  //   comment → always (low-stakes)
  //   badge   → always (celebratory, low-stakes)
  //   file    → needs workspaces.manage OR task_monitor.view
  const viewableCategories = new Set(['comment', 'badge'])
  if (hasOp(user, 'task_monitor.view')) {
    viewableCategories.add('task')
    viewableCategories.add('file')
  }
  if (hasOp(user, 'work_reports.view')) viewableCategories.add('report')
  if (hasOp(user, 'workspaces.manage')) viewableCategories.add('file')
  const visibleActivity = (data.activity ?? [])
    .filter((ev) => !ev.category || viewableCategories.has(ev.category))
    .slice(0, 8)
  // Render the activity section only if the user has visibility
  // beyond bare comments — comments alone for a Sub Admin with no
  // operational grants would just be noise.
  const showActivity =
    hasOp(user, 'task_monitor.view') ||
    hasOp(user, 'work_reports.view') ||
    hasOp(user, 'workspaces.manage')

  // ── Section 5: Quick actions ─────────────────────────────────────
  const actions = ACTION_CATALOGUE.filter((a) => hasOp(user, a.op))

  // Adaptive KPI grid — 1/2/3/4 columns so 3 tiles don't leave one
  // big empty slot on a 4-up grid.
  const kpiColsClass =
    kpis.length === 1
      ? 'grid-cols-1'
      : kpis.length === 2
        ? 'grid-cols-1 md:grid-cols-2'
        : kpis.length === 3
          ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'

  return (
    <>
      {/* ─── Section 1: KPI strip ──────────────────────────────────── */}
      {kpis.length > 0 ? (
        <div className={`grid ${kpiColsClass} gap-4`}>
          {kpis.map((k) => (
            <KpiTile key={k.key} {...k} />
          ))}
        </div>
      ) : null}

      {/* ─── Section 2: Today / Action Needed ──────────────────────── */}
      {showAtRisk || showMissingReports ? (
        <>
          <SectionHeader title="Action Needed" hint="Triage these first" />
          <div
            className={
              'grid gap-4 ' +
              (showAtRisk && showMissingReports
                ? 'grid-cols-1 lg:grid-cols-2'
                : 'grid-cols-1')
            }
          >
            {showAtRisk ? (
              <Card
                icon={AlertCircle}
                iconTone="rose"
                title="At-Risk Tasks"
                subtitle="Overdue, due today, or stale — top 5"
              >
                {atRiskTasks.length === 0 ? (
                  <div className="py-4 text-center">
                    <CheckCircle2
                      size={24}
                      className="mx-auto mb-2 text-[color:var(--color-success)]"
                      aria-hidden="true"
                    />
                    <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
                      All clear — no overdue or stale tasks.
                    </p>
                  </div>
                ) : (
                  <>
                    <ul className="divide-y divide-[color:var(--color-rule)]">
                      {atRiskTasks.map((t) => (
                        <li key={t.id}>
                          <AtRiskRow task={t} />
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/task-monitor"
                      className="mt-3 inline-flex items-center gap-1 text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline"
                    >
                      Open Task Monitor
                      <ArrowRight size={12} />
                    </Link>
                  </>
                )}
              </Card>
            ) : null}
            {showMissingReports ? (
              <Card
                icon={FileText}
                iconTone="sky"
                title="Missing Reports Today"
                subtitle="Daily reports yet to land"
              >
                {todayReports.eligible === 0 ? (
                  <p className="py-4 text-center text-sm text-[color:var(--color-ink-muted)]">
                    No report-eligible members yet.
                  </p>
                ) : missingCount === 0 ? (
                  <div className="py-4 text-center">
                    <CheckCircle2
                      size={24}
                      className="mx-auto mb-2 text-[color:var(--color-success)]"
                      aria-hidden="true"
                    />
                    <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
                      Everyone submitted today.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-display text-4xl text-[color:var(--color-ink-strong)] tabular-nums">
                      {formatNumber(missingCount)}
                    </p>
                    <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
                      of {formatNumber(todayReports.eligible)}{' '}
                      eligible haven't submitted today's daily report.
                    </p>
                    <Link
                      to="/work-reports"
                      className="mt-3 inline-flex items-center gap-1 text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline"
                    >
                      Review reports
                      <ArrowRight size={12} />
                    </Link>
                  </div>
                )}
              </Card>
            ) : null}
          </div>
        </>
      ) : null}

      {/* ─── Section 3: Grant-gated analytics ──────────────────────── */}
      {showAnalytics ? (
        <>
          <SectionHeader
            title="Analytics"
            hint="Breakdowns for the areas you manage"
          />

          {/* Task analytics — task_monitor.view */}
          {showTaskAnalytics ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AnalyticsPanel
                  title="Task Completion"
                  subtitle={`${formatNumber(taskTotal)} total`}
                >
                  <div className="flex flex-col items-center justify-center gap-4 py-2">
                    <ProgressRing
                      percent={taskTotal ? (taskCompleted / taskTotal) * 100 : 0}
                      size={120}
                      stroke={12}
                    />
                    <div className="text-center">
                      <p className="text-sm font-medium text-[color:var(--color-ink-strong)] tabular-nums">
                        {formatNumber(taskCompleted)} completed
                      </p>
                      <p className="mt-0.5 text-[13px] text-[color:var(--color-ink-muted)] tabular-nums">
                        {/* `openCount` = first-column "To Do" count, not every
                         *  unfinished task — label it so (matches the To Do tile). */}
                        {formatNumber(data.tasks?.openCount ?? 0)} to do ·{' '}
                        {formatNumber(data.tasks?.overdueCount ?? 0)} overdue
                      </p>
                    </div>
                  </div>
                </AnalyticsPanel>
                <AnalyticsPanel title="Task Health">
                  <TasksByStatusDonut data={data} />
                </AnalyticsPanel>
              </div>
              {/* Tasks by Status — same payload + shared helper as the
               *  Company Admin card: tasks created each day, split by the
               *  workflow status they are in NOW, in the workflow colours. */}
              <TasksByStatusTrend tasks={data.tasks} />
              <AnalyticsPanel
                title="Tasks by Department · 30 Days"
                subtitle={`${formatNumber(
                  (data.productivityByDepartment ?? []).reduce((a, d) => a + (d.tasksCompleted ?? 0), 0),
                  )} completed`}
              >
                {/* Slightly thinner bars, matching the Company Admin card
                 *  (on request, 2026-10-07). */}
                <RankedBarChart
                  size="slim"
                  rows={(data.productivityByDepartment ?? [])
                    .slice()
                    .sort((a, b) => (b.tasksCompleted ?? 0) - (a.tasksCompleted ?? 0))
                    .map((d) => ({
                      label: d.departmentName,
                      value: d.tasksCompleted ?? 0,
                    }))}
                  formatValue={(v) =>
                    `${formatNumber(v)} ${v === 1 ? 'task' : 'tasks'}`
                  }
                  emptyMessage="No department task data yet"
                />
              </AnalyticsPanel>
            </div>
          ) : null}

          {/* Report compliance — work_reports.view */}
          {showReportAnalytics ? (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
              <ComplianceRing
                label="Daily — today"
                rate={data.reports.dailyToday.complianceRate}
                submitted={data.reports.dailyToday.submitted}
                eligible={data.reports.dailyToday.eligible}
                tone={complianceTone(data.reports.dailyToday.complianceRate)}
              />
              <ComplianceRing
                label="Weekly — this week"
                rate={data.reports.weeklyThisWeek.complianceRate}
                submitted={data.reports.weeklyThisWeek.submitted}
                eligible={data.reports.weeklyThisWeek.eligible}
                tone={complianceTone(data.reports.weeklyThisWeek.complianceRate)}
              />
              <ComplianceRing
                label="Monthly — this month"
                rate={data.reports.monthlyThisMonth.complianceRate}
                submitted={data.reports.monthlyThisMonth.submitted}
                eligible={data.reports.monthlyThisMonth.eligible}
                tone={complianceTone(data.reports.monthlyThisMonth.complianceRate)}
              />
            </div>
          ) : null}

          {/* Member analytics — members.manage */}
          {showMemberAnalytics ? (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnalyticsPanel title="Members by Work Role">
                <MembersByRoleDonut data={data} />
              </AnalyticsPanel>
              <AnalyticsPanel title="Daily Active Users" subtitle="Last 14 days">
                <TrendChart
                  labels={(data.members?.activeUsersSparkline ?? []).map(
                    (p) => p.day,
                  )}
                  series={[
                    {
                      name: 'Active users',
                      color: 'var(--color-accent-strong)',
                      data: (data.members?.activeUsersSparkline ?? []).map(
                        (p) => p.count,
                      ),
                    },
                  ]}
                  labelFormat={dayLabel}
                  emptyMessage="No active users in the last 14 days"
                />
              </AnalyticsPanel>
            </div>
          ) : null}

          {/* SOP usage — sops.manage */}
          {showSopAnalytics ? (
            <AnalyticsPanel className="mt-4" title="SOP Usage">
              <SopUsageBody sops={data.sops} />
            </AnalyticsPanel>
          ) : null}
        </>
      ) : null}

      {/* ─── Section 4: Recent activity ────────────────────────────── */}
      {showActivity ? (
        <>
          <SectionHeader
            title="Recent Activity"
            hint="Filtered to events you can see"
          />
          <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
            {visibleActivity.length === 0 ? (
              <div className="py-8 text-center">
                <Sparkles
                  size={20}
                  className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]"
                />
                <p className="text-sm font-normal text-[color:var(--color-ink-strong)]">
                  Quiet for now
                </p>
                <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
                  Events you have visibility into will appear here.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-[color:var(--color-rule)]">
                {visibleActivity.map((ev, i) => {
                  const Icon = iconForKind(ev.kind)
                  const tone = toneForKind(ev.kind)
                  const Inner = (
                    <div className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                      <span
                        className={
                          'mt-0.5 h-7 w-7 rounded-full flex items-center justify-center shrink-0 ' +
                          (TONE_CLASSES[tone] ?? TONE_CLASSES.slate)
                        }
                      >
                        <Icon size={13} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[color:var(--color-ink-strong)] truncate">
                          {ev.title}
                        </p>
                        <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
                          {fmtRelative(ev.at ?? null)}
                        </p>
                      </div>
                    </div>
                  )
                  return (
                    <li key={`${ev.kind}-${ev.at ?? i}-${i}`}>
                      {ev.link ? <Link to={ev.link}>{Inner}</Link> : Inner}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </>
      ) : null}

      {/* ─── Section 5: Quick Actions ──────────────────────────────
       *  Single-column numbered list — visual parity with the Company
       *  Admin Dashboard's `QuickActions` card. Each row gets a
       *  sequential step badge (1, 2, 3…) regardless of which grants
       *  are present, so the eye reads the column as an ordered list
       *  rather than a 2-D grid of disconnected shortcuts. */}
      {actions.length > 0 ? (
        <>
          <SectionHeader title="Quick Actions" hint="Shortcuts to what you can do" />
          <ol className="flex flex-col gap-2">
            {actions.map((a, idx) => (
              <li key={a.op}>
                <ActionTile n={idx + 1} label={a.label} to={a.to} />
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </>
  )
}

// ---------- Page ----------

export function SubAdminDashboard() {
  const user = useAuthStore((s) => s.user)
  const tenant = useAuthStore((s) => s.tenant)
  const refreshUser = useAuthStore((s) => s.refreshUser)

  // Refresh the cached user payload from /auth/me on every dashboard
  // mount + when the window regains focus. Otherwise a Sub Admin who
  // was already signed-in when the Company Admin granted/revoked their
  // `subAdminOperations` continues to see their stale set until the
  // next full reload — the dashboard would falsely show "No operations
  // assigned yet" or, worse, expose sections for revoked grants. This
  // small refetch closes the cross-session sync gap without needing a
  // WebSocket push channel.
  useEffect(() => {
    refreshUser().catch(() => {
      // Silent failure — keep existing cached user on transient errors.
    })
    function onFocus() {
      refreshUser().catch(() => {})
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refreshUser])

  // Treat a Sub Admin with no grants as a special case — there's
  // nothing to render and no payload to fetch (every section would be
  // hidden). Skip the network round-trip and show a polite empty
  // state instead.
  const hasAnyGrant =
    Array.isArray(user?.subAdminOperations) &&
    user.subAdminOperations.length > 0

  const overviewQuery = useQuery({
    queryKey: ['admin-overview'],
    queryFn: async () => {
      const res = await api.get('/admin/overview')
      // safeParse, mirroring DashboardPage on the SAME endpoint + payload: a
      // single drifting field (e.g. a non-back-filled report-schedule field or
      // a legacy id failing the strict uuid check) must not blank the whole
      // dashboard. Transport errors still reject via axios; a schema mismatch
      // logs the offending paths and renders from the raw payload (every card
      // guards its inputs). Also shares the 'admin-overview' cache entry, so
      // both pages now resolve it identically.
      const parsed = adminOverviewResponse.safeParse(res.data)
      if (parsed.success) return parsed.data
      const paths = parsed.error.issues
        .map((i) => i.path.join('.') || '(root)')
        .join(', ')
      console.warn(
        '[sub-admin dashboard] /admin/overview response did not match the ' +
          'expected schema; rendering from the raw payload. Drifting field(s): ' +
          paths,
      )
      return res.data
    },
    refetchInterval: 60_000,
    enabled: hasAnyGrant,
  })

  return (
    <div>
      {/* No breadcrumb — a root dashboard's crumb is just its own
       *  non-clickable name; the Company Admin, Master Admin and User
       *  dashboards all omit it (QA flagged the inconsistency). */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-medium text-[color:var(--color-ink-strong)]">
              Good {timeOfDay()}, {greetingName(user?.fullName)}
            </h1>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[13px] font-normal border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)] shrink-0">
              Sub Admin
            </span>
          </div>
          <p className="mt-1 text-[color:var(--color-ink-muted)]">
            What's happening at{' '}
            {tenant?.name ?? 'your company'}.
          </p>
        </div>
      </div>

      {!hasAnyGrant ? (
        <div className="rounded-xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-10 text-center">
          <ShieldCheck
            size={32}
            className="mx-auto mb-3 text-[color:var(--color-ink-subtle)]"
            aria-hidden="true"
          />
          <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
            No operations assigned yet
          </h2>
          <p className="mt-2 text-sm text-[color:var(--color-ink-muted)] max-w-md mx-auto">
            Your Company Admin hasn't delegated any operations to you
            yet. Reach out to them so they can grant the areas you need
            access to.
          </p>
          <Link
            to="/profile"
            className="mt-4 inline-flex items-center gap-1 text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline"
          >
            View your profile
            <ArrowRight size={12} />
          </Link>
        </div>
      ) : overviewQuery.data ? (
        // [QA-14] Cached data first: a failed background refetch keeps the
        // last good numbers on screen with a non-blocking notice.
        <>
          {overviewQuery.isError ? (
            <RefreshFailedNotice
              error={overviewQuery.error}
              onRetry={() => overviewQuery.refetch()}
            />
          ) : null}
          <SubAdminBody data={overviewQuery.data} user={user} />
        </>
      ) : overviewQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading dashboard…
        </p>
      ) : overviewQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(overviewQuery.error).message}
        </p>
      ) : null}
    </div>
  )
}
